/**
 * Records the live game's attempts as RunRecordings, by observing the
 * simulation (its per-tick input observer and its events); it never changes
 * what the simulation does.
 *
 * A capture begins whenever the player is placed at a spawn by a new attempt
 * (restart, mode change, or the respawn after a full-run death), because
 * PlayerState.respawn resets every transient and rule state: a fresh
 * simulation placed at the same spawn is in an identical state, so the
 * captured inputs reproduce the run. Within a capture, deaths and practice
 * respawns are the simulation's own deterministic doing and need nothing
 * extra; a manual practice restart to the last checkpoint is the one thing
 * that is not an input, so it is stored as a command.
 *
 * Walking back into the start area resets the attempt without placing the
 * player, so the capture simply continues: the recording then contains a
 * pre-roll before the timed attempt, which a replay reproduces like any other
 * tick. A capture longer than MAX_RUN_TICKS is dropped (no recording).
 */
import type { GameMode } from '../gameplay/AttemptManager';
import type { InputFrame } from '../input/InputFrame';
import type { Simulation } from '../simulation/Simulation';
import type { SpawnPoint } from '../world/LevelData';
import { stateHash } from '../devtools/Recorder';
import type { InputTape } from './InputTape';
import { APP_VERSION, HASH_INTERVAL, MAX_RUN_TICKS, RECORDING_FORMAT, RECORDING_VERSION, SIMULATION_VERSION, type RunCommand, type RunOutcome, type RunRecording } from './RecordingFormat';
import type { IneligibleReason, Split } from './RunTiming';

export interface CaptureInfo {
  trackId: string;
  title: string;
  ruleset: string;
  /** Eligibility of a finished attempt (GameSession's rules, so the recording says what the game said). */
  ineligible(finish: { mode: GameMode; deaths: number }): IneligibleReason[];
}

export interface CaptureListener {
  /** An attempt that started its timer ended (finished, or replaced by a new attempt). */
  onRecording(rec: RunRecording): void;
}

class GrowableTape {
  length = 0;
  moveX = new Float64Array(4096);
  moveZ = new Float64Array(4096);
  yaw = new Float64Array(4096);
  pitch = new Float64Array(4096);
  flags = new Uint8Array(4096);

  push(f: Readonly<InputFrame>): void {
    if (this.length === this.flags.length) this.grow();
    const i = this.length++;
    this.moveX[i] = f.moveX;
    this.moveZ[i] = f.moveZ;
    this.yaw[i] = f.yaw;
    this.pitch[i] = f.pitch;
    this.flags[i] = (f.jumpHeld ? 1 : 0) | (f.jumpPressed ? 2 : 0) | (f.crouchHeld ? 4 : 0);
  }

  /** Copy of the first `length` ticks. */
  snapshot(): InputTape {
    const n = this.length;
    return { length: n, moveX: this.moveX.slice(0, n), moveZ: this.moveZ.slice(0, n), yaw: this.yaw.slice(0, n), pitch: this.pitch.slice(0, n), flags: this.flags.slice(0, n) };
  }

  private grow(): void {
    const n = this.flags.length * 2;
    const f64 = (a: Float64Array): Float64Array<ArrayBuffer> => {
      const b = new Float64Array(n);
      b.set(a);
      return b;
    };
    this.moveX = f64(this.moveX);
    this.moveZ = f64(this.moveZ);
    this.yaw = f64(this.yaw);
    this.pitch = f64(this.pitch);
    const flags = new Uint8Array(n);
    flags.set(this.flags);
    this.flags = flags;
  }
}

export class RunCapture {
  private tape = new GrowableTape();
  private active = false;
  private attempt = -1;
  private mode: GameMode = 'practice';
  private spawn: SpawnPoint = { position: [0, 0, 0], yawDeg: 0 };
  private recordKey = '';
  private commands: RunCommand[] = [];
  private hashes: string[] = [];
  /** The current attempt started its timer (worth keeping as "previous attempt"). */
  private timed = false;
  private pendingFinish: { seconds: number; deaths: number; mode: GameMode } | null = null;
  /** Inside Simulation.step (between the input observer and afterTick). */
  private stepping = false;
  /** State hash after the last completed tick (an attempt replaced mid-step ends there). */
  private lastHash = '';
  private splits: Split[] = [];
  private deaths = 0;
  /** Splits and deaths of the timed attempt that was reset last (until the next attempt starts). */
  private closed: { splits: Split[]; deaths: number } | null = null;
  private readonly unsubscribe: (() => void)[] = [];

  constructor(
    private readonly sim: Simulation,
    private readonly info: CaptureInfo,
    private readonly listener: CaptureListener,
  ) {
    sim.inputObserver = (input) => this.capture(input);
    this.unsubscribe.push(
      sim.events.on('respawn', (e) => {
        if (e.reason !== 'death' || e.attempt !== this.attempt) {
          if (e.attempt === this.attempt && this.active) this.command();
          else this.begin(e.attempt, e.spawn);
        }
      }),
      sim.events.on('attempt-start', () => {
        this.timed = true;
        this.closed = null;
      }),
      sim.events.on('attempt-reset', () => {
        // A full-run death resets the attempt before the respawn that ends this capture: keep what it reached.
        if (this.timed) this.closed = { splits: this.splits, deaths: this.deaths };
        this.splits = [];
        this.deaths = 0;
      }),
      sim.events.on('checkpoint', (e) => this.splits.push({ index: e.index, time: e.split })),
      sim.events.on('death', () => this.deaths++),
      sim.events.on('finish', (e) => {
        this.pendingFinish = { seconds: e.seconds, deaths: e.deaths, mode: e.mode };
      }),
      sim.events.on('finish-unrecorded', () => {
        this.timed = true;
      }),
    );
    this.begin(sim.attempts.attempt, sim.level.data.start);
  }

  /** Ticks captured so far in the current capture. */
  get length(): number {
    return this.active ? this.tape.length : 0;
  }

  /** Call after every simulation tick (state hashes and finishing need the completed tick). */
  afterTick(): void {
    this.stepping = false;
    if (!this.active) return;
    const n = this.tape.length;
    const hash = this.timed || n % HASH_INTERVAL === 0 ? stateHash(this.sim) : '';
    if (n > 0 && n % HASH_INTERVAL === 0) this.hashes.push(hash);
    if (this.timed) this.lastHash = hash;
    const fin = this.pendingFinish;
    if (fin) {
      this.pendingFinish = null;
      this.emit('finished', fin.seconds, fin.deaths, this.info.ineligible(fin));
      this.timed = false;
    }
  }

  dispose(): void {
    for (const u of this.unsubscribe) u();
    if (this.sim.inputObserver) this.sim.inputObserver = null;
    this.active = false;
  }

  private begin(attempt: number, spawn: SpawnPoint): void {
    if (this.active && this.timed && this.pendingFinish === null) {
      // Placed during a step (respawn after a death): that step's input was never applied to the old attempt.
      if (this.stepping) this.tape.length--;
      const closed = this.closed;
      if (closed) {
        this.splits = closed.splits;
        this.deaths = closed.deaths;
      }
      this.emit('incomplete', null, this.deaths, []);
    }
    this.active = true;
    this.attempt = attempt;
    this.mode = this.sim.mode;
    this.spawn = { position: [...spawn.position], yawDeg: spawn.yawDeg };
    this.recordKey = this.sim.recordKey();
    this.tape = new GrowableTape();
    this.commands = [];
    this.hashes = [];
    this.timed = false;
    this.pendingFinish = null;
    this.lastHash = '';
    this.splits = [];
    this.deaths = 0;
    this.closed = null;
  }

  private command(): void {
    this.commands.push({ tick: this.tape.length, kind: 'restart' });
  }

  private capture(input: Readonly<InputFrame>): void {
    this.stepping = true;
    if (!this.active) return;
    if (this.tape.length >= MAX_RUN_TICKS || this.sim.recordKey() !== this.recordKey) {
      // Too long to keep, or the movement rules changed under it: this attempt cannot be replayed.
      this.active = false;
      return;
    }
    this.tape.push(input);
  }

  private emit(outcome: RunOutcome, seconds: number | null, deaths: number, ineligible: IneligibleReason[]): void {
    const ticks = this.tape.length;
    const finalHash = outcome === 'finished' ? stateHash(this.sim) : this.lastHash;
    if (ticks === 0 || finalHash === '') return;
    const hashes = this.hashes.slice(0, Math.floor(ticks / HASH_INTERVAL));
    this.listener.onRecording({
      format: RECORDING_FORMAT,
      version: RECORDING_VERSION,
      build: { simulation: SIMULATION_VERSION, app: APP_VERSION },
      track: { id: this.info.trackId, title: this.info.title, recordKey: this.recordKey, ruleset: this.info.ruleset },
      config: { tickRate: this.sim.config.simulation.tickRate, respawnDelayTicks: this.sim.respawnDelayTicks },
      start: { mode: this.mode, spawn: { position: [...this.spawn.position], yawDeg: this.spawn.yawDeg } },
      ticks,
      inputs: this.tape.snapshot(),
      // A restart issued after the last recorded tick never acts within this recording.
      commands: this.commands.filter((c) => c.tick < ticks).map((c) => ({ ...c })),
      result: { outcome, seconds, splits: this.splits.map((s) => ({ ...s })), deaths, ineligible },
      hashes,
      finalHash,
      meta: { name: '', createdAt: Date.now(), origin: 'local' },
    });
  }
}
